import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Product-page bundle offer.
 * Adds the active product variant and one complementary variant together,
 * then optionally applies a configured Shopify discount code.
 */
class ProductBundleOffer extends HTMLElement {
  #button;
  #select;
  #picker;
  #trigger;
  #options;
  #dialog;
  #product;
  #variantUpdateHandler;
  #selectHandler;
  #clickHandler;
  #triggerHandler;
  #optionHandler;
  #pickerKeydownHandler;
  #outsideHandler;
  #modalTriggerHandler;

  connectedCallback() {
    this.#button = this.querySelector('[data-add-bundle]');
    this.#select = this.querySelector('[data-companion-select]');
    this.#picker = this.querySelector('[data-companion-picker]');
    this.#trigger = this.querySelector('[data-companion-trigger]');
    this.#options = this.querySelector('[data-companion-options]');
    this.#dialog = this.querySelector('[data-companion-dialog]');

    this.#selectHandler = () => this.#handleCompanionChange();
    this.#clickHandler = () => this.#addBundle();
    this.#variantUpdateHandler = (event) => this.#handleMainVariantUpdate(event);
    this.#triggerHandler = () => this.#setPickerOpen(this.#trigger?.getAttribute('aria-expanded') !== 'true');
    this.#optionHandler = (event) => this.#handleCustomOption(event);
    this.#pickerKeydownHandler = (event) => this.#handlePickerKeydown(event);
    this.#outsideHandler = (event) => {
      if (!this.contains(event.target)) this.#setPickerOpen(false);
    };
    this.#modalTriggerHandler = (event) => {
      if (!(event.target instanceof Element)) return;

      const descriptionToggle = event.target.closest('[data-companion-modal-description-toggle]');
      if (descriptionToggle instanceof HTMLButtonElement) {
        const description = this.querySelector('[data-companion-modal-description]');
        if (!description) return;

        const expanded = description.dataset.expanded === 'true';
        description.dataset.expanded = String(!expanded);
        descriptionToggle.setAttribute('aria-expanded', String(!expanded));
        descriptionToggle.textContent = expanded ? 'Ver más' : 'Ver menos';
        return;
      }

      if (!event.target.closest('[data-companion-modal-trigger]')) return;
      event.preventDefault();
      this.#dialog?.showDialog?.();
    };

    this.#select?.addEventListener('change', this.#selectHandler);
    this.#button?.addEventListener('click', this.#clickHandler);
    this.#trigger?.addEventListener('click', this.#triggerHandler);
    this.#options?.addEventListener('click', this.#optionHandler);
    this.#picker?.addEventListener('keydown', this.#pickerKeydownHandler);
    this.addEventListener('click', this.#modalTriggerHandler);
    document.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    document.addEventListener('pointerdown', this.#outsideHandler);

    this.#initialize();
  }

  disconnectedCallback() {
    this.#select?.removeEventListener('change', this.#selectHandler);
    this.#button?.removeEventListener('click', this.#clickHandler);
    this.#trigger?.removeEventListener('click', this.#triggerHandler);
    this.#options?.removeEventListener('click', this.#optionHandler);
    this.#picker?.removeEventListener('keydown', this.#pickerKeydownHandler);
    this.removeEventListener('click', this.#modalTriggerHandler);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    document.removeEventListener('pointerdown', this.#outsideHandler);
  }

  async #initialize() {
    if (this.dataset.enabled !== 'true') {
      this.hidden = true;
      return;
    }

    const json = this.querySelector('[data-companion-json]')?.textContent?.trim();
    if (json && json !== 'null') {
      try {
        this.#setCompanionProduct(JSON.parse(json), false);
        return;
      } catch (error) {
        console.error('[product-bundle-offer] Invalid product data', error);
      }
    }

    await this.#loadRecommendation();
  }

  async #loadRecommendation() {
    const productId = this.dataset.mainProductId;
    const endpoint = this.dataset.recommendationsUrl || '/recommendations/products.json';
    if (!productId) return;

    const storageKey = `vairo-bundle-companion:${productId}`;
    try {
      const cached = window.localStorage.getItem(storageKey);
      if (cached) {
        const cachedProduct = JSON.parse(cached);
        if (cachedProduct?.id && String(cachedProduct.id) !== String(productId)) {
          this.#setCompanionProduct(cachedProduct, true);
          this.hidden = false;
          return;
        }
      }
    } catch (error) {
      console.warn('[product-bundle-offer] Cached companion unavailable', error);
    }

    try {
      const jsonEndpoint = endpoint.endsWith('.json') ? endpoint : `${endpoint}.json`;
      const url = `${jsonEndpoint}?intent=complementary&product_id=${encodeURIComponent(productId)}&limit=10`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const recommended = data.products?.find((item) => String(item.id) !== String(productId));
      if (!recommended) return;

      this.#setCompanionProduct(recommended, true);
      this.hidden = false;
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(recommended));
      } catch (error) {
        console.warn('[product-bundle-offer] Could not cache companion', error);
      }
    } catch (error) {
      console.error('[product-bundle-offer] Recommendation unavailable', error);
    }
  }

  #setCompanionProduct(product, render) {
    this.#product = product;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstAvailable = variants.find((variant) => variant.available !== false) || variants[0];
    if (!firstAvailable) return;

    this.#updateModalProduct(product);

    if (render) {
      for (const title of this.querySelectorAll('[data-companion-title], [data-companion-heading]')) {
        title.textContent = product.title || '';
      }
      for (const image of this.querySelectorAll('.product-bundle-offer__product--companion .product-bundle-offer__image, .product-bundle-offer__stack-image--companion, .product-bundle-offer__modal-image')) {
        if (!(image instanceof HTMLImageElement)) continue;
        image.src = this.#imageUrl(firstAvailable.featured_image || product.featured_image);
        image.alt = image.classList.contains('product-bundle-offer__stack-image') ? '' : (product.title || '');
        image.hidden = !image.src;
      }

      if (this.#select instanceof HTMLSelectElement) {
        this.#select.replaceChildren();
        for (const variant of variants) {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.dataset.price = String(variant.price || 0);
          option.dataset.available = String(variant.available !== false);
          option.disabled = variant.available === false;
          option.selected = String(variant.id) === String(firstAvailable.id);
          option.textContent = `${variant.title}${variant.available === false ? ' — Agotado' : ''}`;
          this.#select.append(option);
        }
        this.#select.hidden = true;
      }

      this.#renderVariantOptions(variants, firstAvailable.id);
    }

    this.#handleCompanionChange();
  }

  #updateModalProduct(product) {
    const title = product.title || 'Accesorio';
    const modalTitle = this.querySelector('[data-companion-modal-title]');
    if (modalTitle) modalTitle.textContent = title;

    const rawDescription = product.description || product.body_html || product.description_html || '';
    const description = document.createElement('div');
    description.innerHTML = rawDescription;
    const modalDescription = this.querySelector('[data-companion-modal-description]');
    const descriptionText = description.textContent?.trim() || '';
    if (modalDescription) {
      modalDescription.textContent = descriptionText;
      modalDescription.hidden = !modalDescription.textContent;
      modalDescription.dataset.expanded = 'false';
    }

    const descriptionToggle = this.querySelector('[data-companion-modal-description-toggle]');
    if (descriptionToggle instanceof HTMLButtonElement) {
      descriptionToggle.hidden = descriptionText.length < 220;
      descriptionToggle.setAttribute('aria-expanded', 'false');
      descriptionToggle.textContent = 'Ver más';
    }

    const modalLink = this.querySelector('[data-companion-modal-link]');
    const productUrl = product.url || (product.handle ? `/products/${product.handle}` : '');
    if (modalLink instanceof HTMLAnchorElement) {
      modalLink.href = productUrl || '#';
      modalLink.hidden = !productUrl;
    }

    for (const trigger of this.querySelectorAll('[data-companion-modal-trigger]')) {
      trigger.setAttribute('aria-label', `Ver detalles de ${title}`);
    }
  }

  #handleCompanionChange() {
    const variant = this.#selectedCompanionVariant();
    if (!variant) return;

    const imageSource = variant.featured_image || this.#product?.featured_image;
    for (const image of this.querySelectorAll('.product-bundle-offer__product--companion .product-bundle-offer__image, .product-bundle-offer__stack-image--companion, .product-bundle-offer__modal-image')) {
      if (!(image instanceof HTMLImageElement) || !imageSource) continue;
      image.src = this.#imageUrl(imageSource);
      image.alt = image.classList.contains('product-bundle-offer__stack-image') ? '' : (this.#product?.title || '');
      image.hidden = false;
    }

    const price = Number(variant.price || 0);
    const accessoryComboPrice = Math.min(this.#accessoryComboPrice(), price);
    const priceText = this.querySelector('[data-companion-price-text]');
    if (priceText) {
      priceText.textContent = `Lo sumás por ${this.#formatMoney(accessoryComboPrice)}`;
    }

    const modalPrice = this.querySelector('[data-companion-modal-price]');
    if (modalPrice) modalPrice.textContent = this.#formatMoney(price);

    const modalComboPrice = this.querySelector('[data-companion-modal-combo-price]');
    if (modalComboPrice) modalComboPrice.textContent = `En este combo: +${this.#formatMoney(accessoryComboPrice)}`;

    this.#syncVariantPicker(variant);
    this.#setPickerOpen(false);
    this.#updateTotals();
    this.#updateAvailability();
  }

  #renderVariantOptions(variants, selectedId) {
    if (!this.#options) return;

    this.#options.replaceChildren();
    for (const variant of variants) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'product-bundle-offer__variant-option';
      option.setAttribute('role', 'option');
      option.setAttribute('data-companion-option', '');
      option.dataset.variantId = String(variant.id);
      option.setAttribute('aria-selected', String(String(variant.id) === String(selectedId)));
      option.disabled = variant.available === false;

      const title = document.createElement('span');
      title.textContent = variant.title || '';
      option.append(title);

      if (variant.available === false) {
        const status = document.createElement('small');
        status.textContent = 'Agotado';
        option.append(status);
      }

      this.#options.append(option);
    }
  }

  #syncVariantPicker(variant) {
    const variants = Array.isArray(this.#product?.variants) ? this.#product.variants : [];
    const hasOptions = variants.length > 1 && !variants.every((item) => item.title === 'Default Title');
    if (this.#picker) this.#picker.hidden = !hasOptions;

    const selected = this.querySelector('[data-companion-selected]');
    if (selected) selected.textContent = variant?.title || '';

    for (const option of this.querySelectorAll('[data-companion-option]')) {
      option.setAttribute('aria-selected', String(option.dataset.variantId === String(variant?.id)));
    }
  }

  #handleCustomOption(event) {
    const option = event.target.closest('[data-companion-option]');
    if (!(option instanceof HTMLButtonElement) || option.disabled || !this.#select) return;

    this.#select.value = option.dataset.variantId || '';
    this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    this.#trigger?.focus();
  }

  #setPickerOpen(open) {
    if (!this.#trigger || !this.#options || this.#picker?.hidden) return;
    this.#trigger.setAttribute('aria-expanded', String(open));
    this.#options.hidden = !open;
  }

  #handlePickerKeydown(event) {
    if (!this.#trigger || !this.#options) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#setPickerOpen(false);
      this.#trigger.focus();
      return;
    }

    const enabledOptions = [...this.#options.querySelectorAll('[data-companion-option]:not(:disabled)')];
    if (!enabledOptions.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;

    event.preventDefault();
    this.#setPickerOpen(true);
    const currentIndex = enabledOptions.indexOf(document.activeElement);
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex < 0
      ? (direction > 0 ? 0 : enabledOptions.length - 1)
      : (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
    enabledOptions[nextIndex].focus();
  }

  #selectedCompanionVariant() {
    const variants = Array.isArray(this.#product?.variants) ? this.#product.variants : [];
    const selectedId = this.#select?.value;
    return variants.find((variant) => String(variant.id) === String(selectedId))
      || variants.find((variant) => variant.available !== false)
      || variants[0];
  }

  #handleMainVariantUpdate(event) {
    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && String(eventProductId) !== String(this.dataset.mainProductId)) return;

    const variant = event.detail?.resource;
    if (!variant?.id) return;

    this.dataset.mainVariantId = String(variant.id);
    this.dataset.mainPrice = String(variant.price || 0);
    this.dataset.mainAvailable = String(variant.available !== false);

    const title = this.querySelector('[data-main-variant-title]');
    if (title) {
      title.textContent = variant.title || '';
      title.hidden = !variant.title || variant.title === 'Default Title';
    }

    const price = this.querySelector('[data-main-price-text]');
    if (price) price.textContent = this.#formatMoney(Number(variant.price || 0));

    const source = variant.featured_media?.preview_image?.src || variant.featured_image?.src || variant.featured_image;
    for (const image of this.querySelectorAll('.product-bundle-offer__product--main .product-bundle-offer__image, .product-bundle-offer__stack-image--main')) {
      if (image instanceof HTMLImageElement && source) image.src = this.#imageUrl(source);
    }

    this.#updateTotals();
    this.#updateAvailability();
  }

  #updateTotals() {
    const mainPrice = Number(this.dataset.mainPrice || 0);
    const companionPrice = Number(this.#selectedCompanionVariant()?.price || 0);
    if (!mainPrice || !companionPrice) return;

    const original = mainPrice + companionPrice;
    const accessoryComboPrice = Math.min(this.#accessoryComboPrice(), companionPrice);
    const bundle = mainPrice + accessoryComboPrice;
    const saving = original - bundle;

    const originalElement = this.querySelector('[data-original-total]');
    const totalElement = this.querySelector('[data-bundle-total]');
    const savingElement = this.querySelector('[data-bundle-saving]');
    if (originalElement) originalElement.textContent = this.#formatMoney(original);
    if (totalElement) totalElement.textContent = this.#formatMoney(bundle);
    if (savingElement) savingElement.textContent = `Ahorrás ${this.#formatMoney(saving)}`;
  }

  #accessoryComboPrice() {
    return Math.max(0, Number(this.dataset.accessoryComboPrice || 0));
  }

  #updateAvailability() {
    const companion = this.#selectedCompanionVariant();
    const mainAvailable = this.dataset.mainAvailable !== 'false';
    const companionAvailable = companion?.available !== false;
    const available = mainAvailable && companionAvailable;
    const label = this.querySelector('[data-button-label]');
    if (this.#button) {
      this.#button.disabled = !available;
      if (!this.#button.dataset.defaultLabel && label?.textContent) {
        this.#button.dataset.defaultLabel = label.textContent;
      }
    }
    if (label && !this.dataset.state) {
      label.textContent = available ? (this.#button?.dataset.defaultLabel || 'Agregar los dos al carrito') : 'Combo sin stock';
    }
  }

  #currentMainVariantId() {
    const localForm = this.closest('.product-details')?.querySelector('product-form-component input[name="id"]');
    return localForm?.value || this.dataset.mainVariantId;
  }

  async #addBundle() {
    const mainVariantId = this.#currentMainVariantId();
    const companion = this.#selectedCompanionVariant();
    if (!mainVariantId || !companion?.id || this.#button?.disabled) return;

    const label = this.querySelector('[data-button-label]');
    const status = this.querySelector('[data-status]');
    const originalLabel = label?.textContent || 'Agregar los dos al carrito';
    const sectionIds = [...new Set(
      [...document.querySelectorAll('cart-items-component')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean)
    )];

    this.dataset.state = 'loading';
    this.#button.disabled = true;
    if (label) label.textContent = 'Agregando…';
    if (status) status.textContent = '';

    let itemsWereAdded = false;
    try {
      const addResponse = await fetch(`${window.Theme?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: Number(mainVariantId), quantity: 1 },
            { id: Number(companion.id), quantity: 1 },
          ],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const addData = await addResponse.json().catch(() => ({}));
      if (!addResponse.ok || addData.status) {
        throw new Error(addData.description || addData.message || 'No pudimos agregar el combo.');
      }
      itemsWereAdded = true;

      let finalData = addData;
      let discountError = false;
      const discountCode = this.dataset.discountCode?.trim();
      if (discountCode) {
        const discountResponse = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: discountCode, sections: sectionIds }),
        });
        const discountData = await discountResponse.json().catch(() => ({}));
        if (discountResponse.ok) {
          finalData = discountData;
          discountError = discountData.discount_codes?.some(
            (discount) => discount.code === discountCode && discount.applicable === false
          );
        } else {
          discountError = true;
        }
      }

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .catch(() => ({}));

      document.dispatchEvent(new CartAddEvent(cart, `bundle-${this.dataset.mainProductId}`, {
        source: 'product-bundle-offer',
        itemCount: cart.item_count,
        productId: this.dataset.mainProductId,
        variantId: String(mainVariantId),
        sections: finalData.sections || addData.sections,
      }));

      this.dataset.state = discountError ? 'warning' : 'success';
      if (label) label.textContent = '¡Combo agregado!';
      if (discountError && status) {
        status.textContent = 'El combo se agregó, pero el código de descuento no está disponible.';
      }
    } catch (error) {
      console.error('[product-bundle-offer] Add failed', error);
      this.dataset.state = 'error';
      if (status) {
        status.textContent = itemsWereAdded
          ? 'Los productos se agregaron, pero no pudimos aplicar la promoción.'
          : (error.message || 'No pudimos agregar el combo. Intentá nuevamente.');
      }
      if (label) label.textContent = itemsWereAdded ? 'Combo agregado' : 'Reintentar';
    } finally {
      window.setTimeout(() => {
        this.dataset.state = '';
        this.#updateAvailability();
        if (label) label.textContent = originalLabel;
      }, itemsWereAdded ? 2200 : 900);
    }
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'ARS';
    const fractionDigits = currency === 'ARS' ? 0 : 2;
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(fractionDigits)}`;
    }
  }

  #imageUrl(value) {
    const source = typeof value === 'string' ? value : value?.src;
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', '240');
      return url.toString();
    } catch {
      return source;
    }
  }
}

if (!customElements.get('product-bundle-offer')) {
  customElements.define('product-bundle-offer', ProductBundleOffer);
}
